import * as React from "react";
import {
    Button,
    FormGroup,
    Grid,
    Switch,
    TextField,
    FormControlLabel, FormControl, InputLabel, Select, MenuItem
} from "@material-ui/core";
import {API} from "aws-amplify";
import JsonRenderComponent from "../../../../utils/JsonRenderComponent";
import {Alert} from "@material-ui/lab";
import {LoadingSpinner} from "../../../../utils/LoadingSpinner";
import {initiateAndActivateProgramEnrollmentDefault, IInitiateAndActivateProgramEnrollmentRequest} from "./request/InitiateAndActivateProgramEnrollmentRequest";

interface IProps {
    data?: any
}

interface IState {
    errorMessage: string
    isJsonInput: boolean
    isLoading: boolean
    jsonInput: string
    request: IInitiateAndActivateProgramEnrollmentRequest
    response: any
}

interface IValidationRule {
    value: string;
    errorMessage: string;
    pattern?: RegExp;
}

enum InputField {
    DRUG_ID,
    PERSON_ID,
    PROFILE_ID,
    CUSTOMER_ID,
    PROGRAM_NAME,
    SCHEDULE_FREQUENCY
}

class InitiateAndActivateProgramEnrollmentSection extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            errorMessage: '',
            isJsonInput: false,
            isLoading: false,
            jsonInput: initiateAndActivateProgramEnrollmentDefault,
            request: {} as IInitiateAndActivateProgramEnrollmentRequest,
            response: ''
        };

        this.toggleJsonInput = this.toggleJsonInput.bind(this);
        this.setRequest = this.setRequest.bind(this);
        this.handleValidation = this.handleValidation.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    public render() {
        return(
            <Grid
                container
                direction="column"
                justify="center"
                alignItems="center"
                spacing={3}
            >

                <Grid item xs={12}>
                    <Grid container spacing={3} justify="center">
                        <Grid item xs>
                            <Alert severity="info">
                                <p>
                                    Currently this page supports only AP_AUTO_REFILL program name and ONE_TIME schedule frequency.
                                </p>
                            </Alert>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs>
                    <FormGroup>
                        <FormControlLabel control={ <Switch onChange={ this.toggleJsonInput }/> } label="Toggle Json Input" />
                    </FormGroup>
                </Grid>


                { !this.state.isJsonInput &&
                    <Grid
                        container
                        direction="column"
                        justify="center"
                        alignItems="center"
                        spacing={3}
                    >
                        <Grid item xs>
                            <FormControl>
                                <InputLabel>Program Name (Required)</InputLabel>
                                <Select
                                    value={this.state.request.programName}
                                    style={{width: 400}}
                                    onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.PROGRAM_NAME, event) }
                                >
                                    <MenuItem value="AP_AUTO_REFILL">
                                        <em>AP_AUTO_REFILL</em>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs>
                            <TextField
                                style={{width: 400}}
                                label="Drug Id (Required)"
                                variant="outlined"
                                value={ this.state.request.drugId || '' }
                                onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.DRUG_ID, event) }
                            />
                        </Grid>
                        <Grid item xs>
                            <TextField
                                style={{width: 400}}
                                label="Person Id (Required)"
                                variant="outlined"
                                value={ this.state.request.personId || '' }
                                onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.PERSON_ID, event) }
                            />
                        </Grid>
                        <Grid item xs>
                            <TextField
                                style={{width: 400}}
                                label="Profile Id (Required)"
                                variant="outlined"
                                value={ this.state.request.profileId || '' }
                                onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.PROFILE_ID, event) }
                            />
                        </Grid>
                        <Grid item xs>
                            <TextField
                                style={{width: 400}}
                                label="Customer Id (Required)"
                                variant="outlined"
                                value={ this.state.request.customerId || '' }
                                onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.CUSTOMER_ID, event) }
                            />
                        </Grid>
                        <Grid item xs>
                            <FormControl>
                                <InputLabel>Schedule Frequency</InputLabel>
                                <Select
                                    value={this.state.request.scheduleFrequency || ''}
                                    style={{width: 400}}
                                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.SCHEDULE_FREQUENCY, event)}
                                >
                                    <MenuItem value="ONE_TIME">
                                        <em>ONE_TIME</em>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                }

                { this.state.isJsonInput &&
                    <Grid item xs>
                        <TextField
                            multiline
                            rows={ 8 }
                            label="Initiate and Activate Program Enrollment Request"
                            variant="filled"
                            value={ this.state.jsonInput }
                            onChange={ this.setRequest }
                            style={{ width: 800 }}
                        />
                    </Grid>
                }

                <Grid item xs>
                    <Button variant="outlined" color="default" onClick={ this.handleSubmit }>
                        Submit
                    </Button>
                </Grid>

                { this.state.isLoading && <LoadingSpinner/> }

                { this.state.errorMessage &&
                    <Alert variant="filled" severity="info">
                        Error encountered: { this.state.errorMessage }
                    </Alert>
                }

                { this.state.response &&
                    <div>
                        <br/>
                        <h4>Status</h4>
                        <p style={{
                            border: '3px solid lightgray',
                            padding: '5px',
                            whiteSpace: "pre-wrap",
                            wordBreak: "normal",
                            wordWrap: "break-word"
                        }}>
                            <JsonRenderComponent jsonObject={ this.state.response } printHtml={ false }/>
                        </p>
                    </div>
                }
            </Grid>
        );
    }

    private toggleJsonInput() {
        this.setState({ isJsonInput: !this.state.isJsonInput });
    }

    private handleValidation(request: IInitiateAndActivateProgramEnrollmentRequest): boolean {
        const validationRules: Record<string, IValidationRule> = {
            personId: {
                value: request.personId,
                errorMessage: 'Please enter the person id',
                pattern: /^amzn[1-9]+[0-9]*\.([A-Za-z0-9]+\.)+A[A-Z0-9]*$/
            },
            profileId: {
                value: request.profileId,
                errorMessage: 'Please enter the profile id',
                pattern: /^amzn[1-9]+[0-9]*.([A-Za-z0-9]+.)+[A-Za-z0-9]*$/
            },
            customerId: {
                value: request.customerId,
                errorMessage: 'Please enter the customerId',
                pattern: /^A[A-Z0-9]*$/
            },
            drugId: {
                value: request.drugId,
                errorMessage: 'Please enter the drug Id',
                pattern: /^[A-Za-z0-9]{24}$/
            },
            programName: {
                value: request.programName,
                errorMessage: 'Please enter the programName'
            }
        };

        return this.validateFields(validationRules);
    }

    private validateFields(rules: Record<string, IValidationRule>): boolean {
        for (const [field, rule] of Object.entries(rules)) {
            if (!rule.value) {
                alert(rule.errorMessage);
                return false;
            }

            if (rule.pattern && !rule.value.match(rule.pattern)) {
                alert(`Please enter the valid ${field}.`);
                return false;
            }
        }

        return true;
    }

    private setRequestFields(field: InputField, event: React.ChangeEvent<HTMLTextAreaElement>) {
        const { value } = event.target;

        // Use object literal to map fields to their corresponding property names
        const fieldMapping: Record<InputField, keyof IInitiateAndActivateProgramEnrollmentRequest> = {
            [InputField.DRUG_ID]: 'drugId',
            [InputField.PERSON_ID]: 'personId',
            [InputField.PROGRAM_NAME]: 'programName',
            [InputField.PROFILE_ID]: 'profileId',
            [InputField.CUSTOMER_ID]: 'customerId',
            [InputField.SCHEDULE_FREQUENCY]: 'scheduleFrequency'
        };

        // Create new request object with spread operator to maintain immutability
        this.setState(
            prevState => ({
                request: {
                    ...prevState.request,
                    [fieldMapping[field]]: value
                }
            }),
            () => console.log(this.state.request)
        );
    }

    private setRequest(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({ jsonInput: event.target.value });
    }

    private handleSubmit() {
        this.setState({isLoading: true});
        this.setState({errorMessage: ''});
        const body: IInitiateAndActivateProgramEnrollmentRequest = this.state.isJsonInput
            ? JSON.parse(this.state.jsonInput)
            : this.state.request;

        const isValid = this.handleValidation(body);

        if (isValid) {
            const config = {
                body,
                headers: {}
            }
            this.callInitateAndActivateEnrollment(config);
        }
        this.setState({isLoading: false});
    }

    private callInitateAndActivateEnrollment(config: any) {
        const apiName = 'ApaqPortalAPI';
        const path = '/pharmacy-customer/initiate-and-activate-program-enrollment';

        console.log(config)
        API.post(apiName, path, config).then((enrollmentResponse: any) => {
            console.log(enrollmentResponse);
            this.setState(prev => ({
                ...prev,
                response: enrollmentResponse,
                errorMessage: ''
            }));
        }).catch((error) => {
            console.log(error);
            this.setState(prev => ({
                ...prev,
                response: '',
                errorMessage: error.response?.data?.message || 'An error occurred'
            }));
        });
    }
}

export default InitiateAndActivateProgramEnrollmentSection;
